import rawContentfulLayoutData from "@/data/contentfulLayoutData.json";
import { LayoutData } from "@/types/layoutData";
import { isNoIndexDoFollow, isNoIndexNoFollow } from "@/utils/general";

import { createSocialMediaMetadata, SocialMediaMetadata } from "./socialMediaMetadata";
import { currentTheme } from "./theming";

// Type for slugFound
const slugFound = (pathname: string, layoutData: LayoutData[]): LayoutData | undefined =>
  layoutData.find((el) => el?.url_slug === pathname);

export const getMetaDataBySlug = (
  pathname: string,
  title: string = "Hair Wellness from Within",
  description: string = "Discover the #1 dermatologist-recommended hair growth supplements for men and women. Clinically proven and 100% drug-free, using patented technology for visibly thicker hair and less shedding.",
  /** @description Pass null to omit this default suffix entirely */
  suffix: string | null = " | Nutrafol",
  imageUrl?: string
): SocialMediaMetadata => {
  // Guard against undefined/null pathname during build
  if (!pathname || typeof pathname !== "string") {
    pathname = "/";
  }

  const layoutDataBySlug = getLayoutDataBySlug(pathname, rawContentfulLayoutData as LayoutData[]);
  const theme = currentTheme(pathname);

  if (theme === "men") {
    suffix = " | Nutrafol Men";
  }

  const fullTitle = title + (suffix ?? "");
  const socialMetadata = createSocialMediaMetadata(fullTitle, description, pathname, imageUrl);

  if (isNoIndexDoFollow(pathname)) {
    return {
      ...socialMetadata,
      robots: "noindex, dofollow",
    };
  }

  if (isNoIndexNoFollow(layoutDataBySlug)) {
    return {
      ...socialMetadata,
      robots: "noindex, nofollow",
    };
  }

  return socialMetadata;
};

export const getMetaDataBySlugMen = (
  pathname: string,
  title: string = "Hair Growth Products For Men",
  description: string = "Discover clinically effective, 100% drug-free hair growth products for men. Target the root causes of thinning with natural ingredients for visibly thicker, healthier hair.",
  imageUrl?: string
) => {
  return getMetaDataBySlug(pathname, title, description, " | Nutrafol Men", imageUrl);
};

export const getLayoutDataBySlug = (
  pathname: string,
  layoutData: LayoutData[]
): LayoutData | undefined => {
  // Guard against undefined/null pathname during build
  if (!pathname || typeof pathname !== "string") {
    return undefined;
  }

  let reference_data = "";

  if (pathname.includes("/sms")) {
    pathname = "/sms/";
  }

  if (pathname.includes("/podcast")) {
    pathname = "/podcast/";
  }

  const reference_data_common =
    '<br/><br/>1. Ablon, G. J Drugs Dermat. 2018. 2. Stephens, T., et al. JCAD. 2022. 3. Ablon, G., et al. JDD, 2021. 4. Berkowitz, S., et al. ASDS. 2020. 5. Auddy, B HJ. JANA. 2008. 6. Beoy, L. Tropical Life Sci Res. 2010. 7. Proksch, E. Skin Pharm Physiology. 2013. 8. Meissner, H.O. IJBS. 2005. 9. Rossi, A. Int. J of Immuno and Pharm. 2012. 10. Pandely, A. Bio Med & Pharm. 2018. 11. Le Floc’h, C. JCD. 2014. 12. Tenore, G. J Med Food. 2016. 13. Jagetia, GC. J of Clinical Immunology. 2007. <span class="vegan">14. Nutrafol. Data on file. 2022. 15. Wandry, F. J Comm Med and Pub Health Rep. 2021.</span>';

  if (pathname.includes("/influencer/hs/")) {
    reference_data = `
        *New customers only. Limit one per customer. Not combinable with other offers. Limited time. 3-Month delivery discount applied to first delivery only. Your subscription will renew every 90 days at $224.`;

    return {
      footer_type: "Simple Footer",
      has_main_padding: false,
      header_background_color: "White",
      header_type: "Really Simple Header",
      no_index_no_follow: true,
      hide_attentive_popup: true,
      reference_data: {
        reference_data: reference_data + reference_data_common,
      },
      url_slug: pathname,
    };
  }

  if (pathname.includes("/influencergwp/")) {
    reference_data = `
New customers only. Limit one per customer. Not combinable with other offers. Limited time. 1-Month delivery discount applied to first delivery only. While supplies last.
<br/><br/>
†${process.env.NEXT_PUBLIC_IQVIA_DATE}
<br/><br/>
1. Ablon, G. J Drugs Dermat. 2018. 2. Stephens, T., et al. JCAD. 2022./Nutrafol. Data on file. 2020. 3. Auddy, B. JANA. 2008. 4. Proksch, E., et al. Skin Pharm Physiology. 2013. 5. Beoy, L. Tropical Life Sci Res. 2010. 6. Jagetia, G. J Clin Immunol. 2007. 7. Nutrafol. Data on file. 2022. 8. Berkowitz, S., et al. ASDS, 2020./Nutrafol. Data on file. 2020. 9. Nutrafol. Data on file. 2023.`;
    return {
      footer_type: "Simple Footer",
      has_main_padding: true,
      header_background_color: "White",
      header_type: "Really Simple Header",
      no_index_no_follow: true,
      hide_attentive_popup: true,
      reference_data: {
        reference_data: reference_data,
      },
      url_slug: pathname,
    };
  }

  if (pathname.includes("/growthinfluencer/") && !pathname.includes("/hairserum/")) {
    reference_data = `
        *New customers only. Limit one per customer. Not combinable with other offers. Limited time. 3-Month delivery discount applied to first delivery only. Your subscription will renew every 90 days at $224.`;

    return {
      footer_type: "Simple Footer",
      has_main_padding: true,
      header_background_color: "White",
      header_type: "Really Simple Header",
      no_index_no_follow: true,
      hide_attentive_popup: true,
      reference_data: {
        reference_data: reference_data + reference_data_common,
      },
      url_slug: pathname,
    };
  }

  if (pathname.startsWith("/tracking/")) {
    return {
      has_main_padding: true,
      header_background_color: "White",
    };
  }

  /*
   * Product comparison LP: no ribbon. On mobile the default header is in-flow, so
   * disable main-top-padding to avoid stacking an empty ::before reserve under the header.
   */
  if (pathname.includes("hair-growth-product-comparison")) {
    return {
      footer_type: "Default",
      has_main_padding: false,
      has_ribbon: false,
      header_background_color: "White",
      header_type: "Default",
      url_slug: pathname,
    };
  }

  const slugObject = slugFound(pathname, layoutData);

  /*
   * NT2-23517 [PROD] Primary nav | ribbon logic
   * All pages using default/primary header should have a ribbon.
   * Exception for the above: hardcoded Partners LPs (and related paths below).
   * Not A/B or cookie-gated in app code; HeaderFactory may further hard-hide paths
   * or apply promo hideOnTarget. Mobile ribbon is in-flow (exact height) to avoid CLS.
   */
  if (slugObject) {
    const exceptions = ["/partner/mlb/terms", "/hair/benefits/services/partners/find-a-partner/"];

    const isException = exceptions.some((exception) => pathname.startsWith(exception));

    slugObject.has_ribbon =
      isException ||
      (!pathname.includes("/partner") &&
        !pathname.includes("/partnership") &&
        !pathname.includes("/feb26-restart") &&
        !pathname.includes("/startgrowingtoday") &&
        !pathname.includes("/podcast"));
  }

  return layoutData && slugObject;
};
